/**
 * useOpenRequest — PROD-1395. "Allow access?" reachable from the case itself.
 *
 * Alex 2026-11-03: a doctor opened the patient's share code, the patient got the push, but the case itself
 * showed nothing and offered nothing to tap. The only ways in were the bell row and Share > hub. This module
 * reads the existing `GET /cases/<id>/open-request` (s119, patient only) for the cases that can hold a request
 * and nothing else:
 *  - only a case shared by code and not yet claimed can hold one (`isCodeSharedUnclaimed`), so a patient pays
 *    one light read for 0 to 2 cases, never a list-wide join;
 *  - `request` in the answer is set only while the request is pending; a lapsed or answered one is null.
 *
 * useOpenRequest(caseId, enabled) — Case Home. Reads on mount, then polls with a setTimeout chain that doubles
 *   from OPEN_REQUEST_POLL_FIRST_MS to the ceiling OPEN_REQUEST_POLL_MAX_MS. Never a flat setInterval (a fixed
 *   period cannot back off; check-poll-budget's defect). A hidden tab reads nothing; coming back reads at once and
 *   resets the delay. A failed read keeps the last value and never throws. A REFUSED read (401 signed out, 403
 *   not this patient's case; the PROD-270 `pollRefused` convention plus 401) ends the chain, because asking again
 *   gets the same answer (R1395 L3). Coming back to the tab, or the next mount, tries once more.
 * useOpenRequestsFor(cases) — the Cases list. Reads once on mount and once each time the page becomes visible,
 *   only for the code-shared unclaimed cases. It does not poll.
 *
 * Held by scripts/check-open-request-entry.mjs (OE-1..OE-4; OE-3(h) and OE-3(i) execute useOpenRequest).
 */
import { useEffect, useState } from 'react'
import { casesAPI } from '../services/api'
import { isCodeSharedUnclaimed } from '../utils/caseShareState'
import { pollRefused } from '../utils/pollRefused'

export const OPEN_REQUEST_POLL_FIRST_MS = 15000
export const OPEN_REQUEST_POLL_BACKOFF = 2
export const OPEN_REQUEST_POLL_MAX_MS = 60000

/** The pending request in a `GET /cases/<id>/open-request` answer, or null. */
export const pendingOf = (d) => (d && d.request ? d.request : null)

/** The ids of the cases that can hold an open request: shared by code, no doctor yet. */
export function openRequestCaseIds(cases) {
  return (Array.isArray(cases) ? cases : [])
    .filter((c) => c && c.id && isCodeSharedUnclaimed(c))
    .map((c) => c.id)
}

const isVisible = () => typeof document === 'undefined' || document.visibilityState === 'visible'

/** R1395 L3: a read the server refused. 403 is PROD-270's pollRefused; 401 (signed out) is refused here too. */
export const openRequestRefused = (e) => pollRefused(e) || e?.status === 401

export function useOpenRequest(caseId, enabled) {
  const [state, setState] = useState({ id: null, request: null })

  useEffect(() => {
    if (!caseId || !enabled) return undefined
    let stopped = false
    let timer = null
    let delay = OPEN_REQUEST_POLL_FIRST_MS
    let refused = false // R1395 L3: the last read was refused; the chain stays off until the tab comes back

    const read = async () => {
      try {
        const d = await casesAPI.getOpenRequest(caseId)
        if (!stopped) setState({ id: caseId, request: pendingOf(d) })
      } catch (e) {
        if (openRequestRefused(e)) refused = true // asking again gets the same answer
        // any failure keeps the last value
      }
    }
    const schedule = () => {
      clearTimeout(timer)
      timer = null
      if (refused) return // a refused poll is over (PROD-270); onVisibility or the next mount tries again
      timer = setTimeout(tick, delay)
    }
    const tick = async () => {
      timer = null
      if (stopped) return
      if (document.visibilityState !== 'visible') return // the visibilitychange listener re-arms
      await read()
      if (stopped) return
      delay = Math.min(delay * OPEN_REQUEST_POLL_BACKOFF, OPEN_REQUEST_POLL_MAX_MS)
      schedule()
    }
    const onVisibility = () => {
      if (stopped || document.visibilityState !== 'visible') return
      clearTimeout(timer)
      timer = null
      delay = OPEN_REQUEST_POLL_FIRST_MS
      refused = false // coming back tries once more; a second refusal stops it again
      read().then(() => { if (!stopped) schedule() })
    }

    if (isVisible()) read().then(() => { if (!stopped) schedule() })
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      stopped = true
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [caseId, enabled])

  return enabled && caseId && state.id === caseId ? state.request : null
}

export function useOpenRequestsFor(cases) {
  const ids = openRequestCaseIds(cases)
  const key = ids.join(',')
  const [byCase, setByCase] = useState({})

  useEffect(() => {
    if (!key) return undefined
    let stopped = false
    const list = key.split(',')
    const readAll = () => {
      if (document.visibilityState !== 'visible') return
      Promise.all(list.map((id) => casesAPI.getOpenRequest(id).then((d) => [id, pendingOf(d)], () => [id, undefined])))
        .then((pairs) => {
          if (stopped) return
          setByCase((prev) => {
            const next = {}
            for (const [id, r] of pairs) {
              const v = r === undefined ? prev[id] : r // a failed read keeps the last value
              if (v) next[id] = v
            }
            return next
          })
        })
    }
    readAll()
    const onVisibility = () => { if (document.visibilityState === 'visible') readAll() }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      stopped = true
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [key])

  const out = {}
  for (const id of ids) if (byCase[id]) out[id] = byCase[id]
  return out
}
